// changed by Cursor: converted Next/TS hook to React JS hook
import React from 'react';
import { useAnimation, useMotionValue } from 'framer-motion';
import { useSetRecoilState } from 'recoil';
import { levelStateAtom } from '../recoil/atoms';

const XP_PER_LEVEL_UNIT = 100;
const BAR_COLOR = '#fecf3a';

export function useAnimateXPBar({
  current_level_prop,
  previous_level_prop,
  delta_level_prop,
}) {
  const bar_controls = useAnimation();
  const icon_controls = useAnimation();
  const xp_gained_controls = useAnimation();
  const bar_scale_controls = useAnimation();

  const setLevelState = useSetRecoilState(levelStateAtom);

  const setActiveDisplayLevel = React.useCallback(
    (level) => {
      setLevelState((prev) => ({
        ...prev,
        currentDisplayLevelForPulse: level,
      }));
    },
    [setLevelState]
  );

  const resetToLevel = React.useCallback(
    (level) => {
      setLevelState({
        currentDisplayLevelForPulse: level,
        isLevelChanging: false,
        levelChangeDirection: 'none',
      });
    },
    [setLevelState]
  );

  const triggerLevelPulse = React.useCallback(
    (level, direction) => {
      setLevelState({
        currentDisplayLevelForPulse: level,
        isLevelChanging: true,
        levelChangeDirection: direction,
      });
      setTimeout(() => {
        setLevelState((prev) => ({
          ...prev,
          isLevelChanging: false,
          levelChangeDirection: 'none',
        }));
      }, 400);
    },
    [setLevelState]
  );

  const initial_prev_frac = React.useMemo(
    () =>
      typeof previous_level_prop === 'number' ? previous_level_prop % 1 : 0,
    [previous_level_prop]
  );
  const initial_progress_bar_width = React.useMemo(
    () => `${initial_prev_frac * 100}%`,
    [initial_prev_frac]
  );

  const bar_width_percent = useMotionValue(initial_progress_bar_width);

  const [current_display_xp_value, setCurrentDisplayXPValue] = React.useState(
    Math.round(initial_prev_frac * XP_PER_LEVEL_UNIT)
  );
  const [display_min_level_text_value, setDisplayMinLevelTextValue] =
    React.useState(
      Math.floor(
        typeof previous_level_prop === 'number' ? previous_level_prop : 0
      )
    );
  const [current_bar_color_value, setCurrentBarColorValue] =
    React.useState(BAR_COLOR);

  const prev_props_ref = React.useRef({
    current_level_prop,
    previous_level_prop,
    delta_level_prop,
  });

  const animate_xp_display = React.useCallback(
    (from_xp, to_xp, duration_seconds) => {
      const start_time = Date.now();
      const animate = () => {
        const elapsed_time = Date.now() - start_time;
        const progress_ratio = Math.min(
          elapsed_time / (duration_seconds * 1000),
          1
        );
        const ease_out_cubic_value = 1 - Math.pow(1 - progress_ratio, 3);
        const current_xp_value = Math.round(
          from_xp + (to_xp - from_xp) * ease_out_cubic_value
        );
        setCurrentDisplayXPValue(Math.max(0, current_xp_value));

        if (progress_ratio < 1) {
          requestAnimationFrame(animate);
        } else {
          setCurrentDisplayXPValue(Math.max(0, to_xp));
        }
      };
      requestAnimationFrame(animate);
    },
    [setCurrentDisplayXPValue]
  );

  const handle_stage_change = React.useCallback(
    (stage, active_level) => {
      setActiveDisplayLevel(active_level);
      setDisplayMinLevelTextValue(Math.floor(active_level));
    },
    [setActiveDisplayLevel]
  );

  React.useEffect(() => {
    const has_props_changed_significantly =
      prev_props_ref.current.current_level_prop !== current_level_prop ||
      prev_props_ref.current.previous_level_prop !== previous_level_prop ||
      prev_props_ref.current.delta_level_prop !== delta_level_prop;

    if (has_props_changed_significantly) {
      const base_level_value = Math.floor(
        typeof previous_level_prop === 'number' ? previous_level_prop : 0
      );
      const prev_level_frac_value =
        typeof previous_level_prop === 'number' ? previous_level_prop % 1 : 0;

      resetToLevel(base_level_value);
      handle_stage_change('filling', base_level_value);
      setCurrentBarColorValue(BAR_COLOR);

      const initial_xp_value = Math.round(
        prev_level_frac_value * XP_PER_LEVEL_UNIT
      );
      setCurrentDisplayXPValue(initial_xp_value);

      const initial_width_value = `${prev_level_frac_value * 100}%`;
      bar_controls.set({ width: initial_width_value });
      bar_width_percent.set(initial_width_value);
      icon_controls.set({
        left: initial_width_value,
        opacity: prev_level_frac_value > 0 ? 1 : 0,
      });

      prev_props_ref.current = {
        current_level_prop,
        previous_level_prop,
        delta_level_prop,
      };
    }
  }, [
    previous_level_prop,
    current_level_prop,
    delta_level_prop,
    resetToLevel,
    handle_stage_change,
    bar_controls,
    bar_width_percent,
    icon_controls,
    setCurrentDisplayXPValue,
    setCurrentBarColorValue,
  ]);

  React.useEffect(() => {
    let is_mounted = true;

    const animate_xp_bar = async () => {
      if (!is_mounted) return;
      await new Promise((resolve) => setTimeout(resolve, 500));
      if (
        !is_mounted ||
        prev_props_ref.current.current_level_prop !== current_level_prop
      )
        return;

      if (
        typeof current_level_prop !== 'number' ||
        typeof previous_level_prop !== 'number'
      ) {
        if (is_mounted) {
          bar_controls.start({ width: '0%', transition: { duration: 0.2 } });
          bar_width_percent.set('0%');
          icon_controls.start({
            left: '0%',
            opacity: 0,
            transition: { duration: 0.1 },
          });
        }
        return;
      }

      const prev_level_int_value = Math.floor(previous_level_prop);
      const current_level_int_value = Math.floor(current_level_prop);
      const prev_level_frac_value = previous_level_prop % 1;
      const current_level_frac_value = current_level_prop % 1;
      const is_level_increase = current_level_int_value > prev_level_int_value;
      const is_level_decrease = current_level_int_value < prev_level_int_value;

      xp_gained_controls.start({
        opacity: [0, 1],
        y: [10, 0],
        transition: { duration: 0.3, ease: 'easeInOut' },
      });

      if (is_level_increase) {
        const level_difference_value =
          current_level_int_value - prev_level_int_value;
        const get_animation_duration = (index_value, is_last_item) => {
          if (level_difference_value <= 2) return is_last_item ? 0.8 : 0.6;
          if (level_difference_value <= 5) return is_last_item ? 1.0 : 0.4;
          const progress_ratio = index_value / level_difference_value;
          if (is_last_item) return 1.2;
          if (progress_ratio < 0.7) return 0.2;
          return 0.4;
        };
        const get_delay_between_levels = (index_value) => {
          if (level_difference_value <= 2) return 150;
          if (level_difference_value <= 5) return 100;
          const progress_ratio = index_value / level_difference_value;
          if (progress_ratio < 0.7) return 50;
          return 100;
        };

        let current_animation_level_value = prev_level_int_value;

        for (let i = 0; i <= level_difference_value; i++) {
          if (
            !is_mounted ||
            prev_props_ref.current.current_level_prop !== current_level_prop
          )
            break;

          const is_first_iteration = i === 0;
          const is_last_iteration = i === level_difference_value;
          const animation_duration_value = get_animation_duration(
            i,
            is_last_iteration
          );

          handle_stage_change('filling', current_animation_level_value);

          if (is_first_iteration) {
            const start_xp_value = Math.round(
              prev_level_frac_value * XP_PER_LEVEL_UNIT
            );
            const fill_time_duration =
              animation_duration_value *
              Math.max(0.3, 1 - prev_level_frac_value);
            if (is_mounted)
              animate_xp_display(
                start_xp_value,
                XP_PER_LEVEL_UNIT,
                fill_time_duration
              );

            await Promise.all([
              bar_controls.start({
                width: '100%',
                transition: {
                  type: 'spring',
                  stiffness: 80,
                  damping: 20,
                  duration: fill_time_duration,
                },
              }),
              icon_controls.start({
                left: '100%',
                opacity: 1,
                transition: {
                  type: 'spring',
                  stiffness: 80,
                  damping: 20,
                  duration: fill_time_duration,
                },
              }),
            ]);
            if (
              !is_mounted ||
              prev_props_ref.current.current_level_prop !== current_level_prop
            )
              break;
            if (is_mounted) bar_width_percent.set('100%');
          } else {
            const target_width_percent_str = is_last_iteration
              ? `${current_level_frac_value * 100}%`
              : '100%';
            const target_xp_for_display = is_last_iteration
              ? Math.round(current_level_frac_value * XP_PER_LEVEL_UNIT)
              : XP_PER_LEVEL_UNIT;

            let fill_time_duration = animation_duration_value;
            if (is_last_iteration) {
              fill_time_duration *=
                current_level_frac_value > 0 ? current_level_frac_value : 0.1;
            }

            if (is_mounted)
              animate_xp_display(0, target_xp_for_display, fill_time_duration);

            await Promise.all([
              bar_controls.start({
                width: target_width_percent_str,
                transition: {
                  type: 'spring',
                  stiffness: is_last_iteration ? 60 : 80,
                  damping: is_last_iteration ? 25 : 20,
                  duration: fill_time_duration,
                },
              }),
              icon_controls.start({
                left: target_width_percent_str,
                opacity: is_last_iteration
                  ? current_level_frac_value > 0
                    ? 1
                    : 0
                  : 1,
                transition: {
                  type: 'spring',
                  stiffness: is_last_iteration ? 60 : 25,
                  damping: is_last_iteration ? 25 : 20,
                  duration: fill_time_duration,
                },
              }),
            ]);
            if (
              !is_mounted ||
              prev_props_ref.current.current_level_prop !== current_level_prop
            )
              break;
            if (is_mounted) bar_width_percent.set(target_width_percent_str);
          }

          if (!is_last_iteration) {
            if (is_mounted) {
              triggerLevelPulse(current_animation_level_value + 1, 'up');

              await bar_scale_controls.start({
                scale: [1, 1.1, 1],
                transition: { duration: 0.3, times: [0, 0.5, 1] },
              });
              if (
                !is_mounted ||
                prev_props_ref.current.current_level_prop !== current_level_prop
              )
                break;

              bar_controls.set({ width: '0%' });
              icon_controls.set({ left: '0%', opacity: 0 });
              if (is_mounted) bar_width_percent.set('0%');
              setCurrentDisplayXPValue(0);
              current_animation_level_value++;
            }
          } else {
            if (is_mounted) {
              handle_stage_change('complete', current_level_int_value);
            }
          }
          if (!is_last_iteration && is_mounted) {
            await new Promise((resolve) =>
              setTimeout(resolve, get_delay_between_levels(i))
            );
          }
        }
      } else if (is_level_decrease) {
        const level_difference_value =
          prev_level_int_value - current_level_int_value;
        const get_animation_duration = (index_value, is_last_item) => {
          if (level_difference_value <= 2) return is_last_item ? 0.8 : 0.6;
          if (level_difference_value <= 5) return is_last_item ? 1.0 : 0.4;
          return is_last_item ? 1.2 : 0.3;
        };

        let current_animation_level_value = prev_level_int_value;

        for (let i = 0; i <= level_difference_value; i++) {
          if (
            !is_mounted ||
            prev_props_ref.current.current_level_prop !== current_level_prop
          )
            break;
          const is_first_iteration = i === 0;
          const is_last_iteration = i === level_difference_value;
          const animation_duration_value = get_animation_duration(
            i,
            is_last_iteration
          );

          handle_stage_change('filling', current_animation_level_value);

          if (is_first_iteration) {
            const start_xp_value = Math.round(
              prev_level_frac_value * XP_PER_LEVEL_UNIT
            );
            const empty_time_duration =
              animation_duration_value * Math.max(0.3, prev_level_frac_value);
            if (is_mounted)
              animate_xp_display(start_xp_value, 0, empty_time_duration);

            await Promise.all([
              bar_controls.start({
                width: '0%',
                transition: {
                  type: 'spring',
                  stiffness: 80,
                  damping: 20,
                  duration: empty_time_duration,
                },
              }),
              icon_controls.start({
                left: '0%',
                opacity: 0,
                transition: {
                  type: 'spring',
                  stiffness: 80,
                  damping: 20,
                  duration: empty_time_duration,
                },
              }),
            ]);
            if (
              !is_mounted ||
              prev_props_ref.current.current_level_prop !== current_level_prop
            )
              break;

            if (
              is_mounted &&
              current_animation_level_value > current_level_int_value
            ) {
              triggerLevelPulse(current_animation_level_value - 1, 'down');
              await bar_scale_controls.start({
                scale: [1, 1.1, 1],
                transition: { duration: 0.3, times: [0, 0.5, 1] },
              });
              if (
                !is_mounted ||
                prev_props_ref.current.current_level_prop !== current_level_prop
              )
                break;
            }

            await new Promise((resolve) => setTimeout(resolve, 50));
            if (
              !is_mounted ||
              prev_props_ref.current.current_level_prop !== current_level_prop
            )
              break;
            current_animation_level_value--;

            if (!is_last_iteration) {
              if (is_mounted) {
                handle_stage_change('filling', current_animation_level_value);
                bar_controls.set({ width: '100%' });
                bar_width_percent.set('100%');
                icon_controls.set({ left: '100%', opacity: 1 });
                setCurrentDisplayXPValue(XP_PER_LEVEL_UNIT);
              }
              await new Promise((resolve) => setTimeout(resolve, 50));
              if (
                !is_mounted ||
                prev_props_ref.current.current_level_prop !== current_level_prop
              )
                break;
            }
          } else if (is_last_iteration) {
            const final_xp_value = Math.round(
              current_level_frac_value * XP_PER_LEVEL_UNIT
            );
            const start_xp_for_final_anim =
              i > 0
                ? XP_PER_LEVEL_UNIT
                : Math.round(prev_level_frac_value * XP_PER_LEVEL_UNIT);
            const decrease_time_duration =
              animation_duration_value *
              Math.max(
                0.3,
                (start_xp_for_final_anim - final_xp_value) / XP_PER_LEVEL_UNIT
              );

            if (is_mounted)
              animate_xp_display(
                start_xp_for_final_anim,
                final_xp_value,
                decrease_time_duration
              );

            await Promise.all([
              bar_controls.start({
                width: `${current_level_frac_value * 100}%`,
                transition: {
                  type: 'spring',
                  stiffness: 60,
                  damping: 25,
                  duration: decrease_time_duration,
                },
              }),
              icon_controls.start({
                left: `${current_level_frac_value * 100}%`,
                opacity: current_level_frac_value > 0 ? 1 : 0,
                transition: {
                  type: 'spring',
                  stiffness: 60,
                  damping: 25,
                  duration: decrease_time_duration,
                },
              }),
            ]);
            if (
              !is_mounted ||
              prev_props_ref.current.current_level_prop !== current_level_prop
            )
              break;
            if (is_mounted)
              handle_stage_change('complete', current_animation_level_value);
          } else {
            const empty_time_duration = animation_duration_value;
            if (is_mounted)
              animate_xp_display(XP_PER_LEVEL_UNIT, 0, empty_time_duration);

            await Promise.all([
              bar_controls.start({
                width: '0%',
                transition: {
                  type: 'spring',
                  stiffness: 100,
                  damping: 15,
                  duration: empty_time_duration,
                },
              }),
              icon_controls.start({
                left: '0%',
                opacity: 0,
                transition: {
                  type: 'spring',
                  stiffness: 100,
                  damping: 15,
                  duration: empty_time_duration,
                },
              }),
            ]);
            if (
              !is_mounted ||
              prev_props_ref.current.current_level_prop !== current_level_prop
            )
              break;

            if (is_mounted) {
              triggerLevelPulse(current_animation_level_value - 1, 'down');
              await bar_scale_controls.start({
                scale: [1, 1.1, 1],
                transition: { duration: 0.3, times: [0, 0.5, 1] },
              });
              if (
                !is_mounted ||
                prev_props_ref.current.current_level_prop !== current_level_prop
              )
                break;
            }

            await new Promise((resolve) => setTimeout(resolve, 30));
            if (
              !is_mounted ||
              prev_props_ref.current.current_level_prop !== current_level_prop
            )
              break;
            current_animation_level_value--;

            if (is_mounted) {
              handle_stage_change('filling', current_animation_level_value);
              bar_controls.set({ width: '100%' });
              bar_width_percent.set('100%');
              icon_controls.set({ left: '100%', opacity: 1 });
              setCurrentDisplayXPValue(XP_PER_LEVEL_UNIT);
            }
            await new Promise((resolve) => setTimeout(resolve, 30));
            if (
              !is_mounted ||
              prev_props_ref.current.current_level_prop !== current_level_prop
            )
              break;
          }
        }
      } else {
        if (!is_mounted) return;
        handle_stage_change('filling', current_level_int_value);
        const target_percentage_value = current_level_frac_value * 100;
        const start_xp_value = Math.round(
          prev_level_frac_value * XP_PER_LEVEL_UNIT
        );
        const target_xp_value = Math.round(
          current_level_frac_value * XP_PER_LEVEL_UNIT
        );
        const same_level_animation_duration = 0.8;

        if (is_mounted)
          animate_xp_display(
            start_xp_value,
            target_xp_value,
            same_level_animation_duration
          );

        await Promise.all([
          bar_controls.start({
            width: `${target_percentage_value}%`,
            transition: {
              type: 'spring',
              stiffness: 70,
              damping: 18,
              duration: same_level_animation_duration,
            },
          }),
          icon_controls.start({
            left: `${target_percentage_value}%`,
            opacity: current_level_frac_value > 0 ? 1 : 0,
            transition: {
              type: 'spring',
              stiffness: 70,
              damping: 18,
              duration: same_level_animation_duration,
            },
          }),
        ]);
        if (!is_mounted) return;
        if (is_mounted)
          handle_stage_change('complete', current_level_int_value);
      }
    };

    animate_xp_bar();

    return () => {
      is_mounted = false;
      bar_controls.stop();
      icon_controls.stop();
      xp_gained_controls.stop();
      bar_scale_controls.stop();
    };
  }, [
    current_level_prop,
    previous_level_prop,
    animate_xp_display,
    handle_stage_change,
    bar_controls,
    icon_controls,
    xp_gained_controls,
    bar_scale_controls,
    bar_width_percent,
    setCurrentDisplayXPValue,
    setCurrentBarColorValue,
    setActiveDisplayLevel,
    resetToLevel,
    triggerLevelPulse,
  ]);

  return {
    bar_controls,
    icon_controls,
    xp_gained_controls,
    bar_scale_controls,
    bar_width_percent,
    current_display_xp_value,
    display_min_level_text_value,
    current_bar_color_value,
  };
}

